/**
 * Module summary:
 * This file exposes hooks for reading one assessment definition and for
 * managing the mutable response/photo state while a user fills out a form.
 */

import { useState, useEffect, useCallback } from 'react';
import { Assessment, ResponseData, QuestionPhoto, QuestionPhotoMap } from '../types/assessment';
import { loadAssessment } from '../services/data/AssessmentLoader';
import { INPUT_LIMITS, sanitizeMultilineDraft } from '../utils/inputSecurity';

interface UseAssessmentReturn {
  assessment: Assessment | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook to load an assessment by ID
 */
export function useAssessment(assessmentId: string | null): UseAssessmentReturn {
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAssessment = useCallback(async () => {
    if (!assessmentId) {
      // Reset the current assessment when no id is selected so old content does not linger on screen.
      setAssessment(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await loadAssessment(assessmentId);
      if (data) {
        setAssessment(data);
      } else {
        // Surface a friendly not-found message when the loader returns no assessment.
        setError(`Assessment "${assessmentId}" not found`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assessment');
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    // Re-run the load whenever the requested assessment id changes.
    fetchAssessment();
  }, [fetchAssessment]);

  return {
    assessment,
    loading,
    error,
    refetch: fetchAssessment
  };
}

interface UseAssessmentResponseReturn {
  responses: ResponseData;
  updateResponse: (questionId: string, value: any) => void;
  updateComment: (questionId: string, comment: string) => void;
  getResponse: (questionId: string) => any;
  getComment: (questionId: string) => string;
  isComplete: (requiredQuestionIds: string[]) => boolean;
  resetResponses: () => void;
  photos: QuestionPhotoMap;
  updatePhotos: (questionId: string, photos: QuestionPhoto[]) => void;
  getPhotos: (questionId: string) => QuestionPhoto[];
}

/**
 * Hook to manage assessment responses
 */
export function useAssessmentResponse(
  initialResponses: ResponseData = {},
  initialPhotos: QuestionPhotoMap = {}
): UseAssessmentResponseReturn {
  const [responses, setResponses] = useState<ResponseData>(initialResponses);
  const [photos, setPhotos] = useState<QuestionPhotoMap>(initialPhotos);

  useEffect(() => {
    // Rehydrate answer state when the user reopens a saved assessment record.
    setResponses(initialResponses);
  }, [initialResponses]);

  useEffect(() => {
    // Rehydrate saved question photos so existing attachments render when returning to the form.
    setPhotos(initialPhotos);
  }, [initialPhotos]);

  const updateResponse = useCallback((questionId: string, value: any) => {
    setResponses(prev => ({
      ...prev,
      // Replace only the targeted question's answer while keeping all other answers intact.
      [questionId]: typeof value === 'string' ? sanitizeMultilineDraft(value, INPUT_LIMITS.questionText) : value
    }));
  }, []);

  const updateComment = useCallback((questionId: string, comment: string) => {
    const commentKey = `${questionId}Comment`;
    setResponses(prev => ({
      ...prev,
      // Store comments under a derived key so each question can keep its answer and note separately.
      [commentKey]: sanitizeMultilineDraft(comment, INPUT_LIMITS.questionComment)
    }));
  }, []);

  const getResponse = useCallback((questionId: string) => {
    return responses[questionId];
  }, [responses]);

  const getComment = useCallback((questionId: string): string => {
    const value = responses[`${questionId}Comment`];
    return typeof value === 'string' ? value : '';
  }, [responses]);

  const isComplete = useCallback((requiredQuestionIds: string[]) => {
    return requiredQuestionIds.every(id => {
      // Check every required question so incomplete forms can be flagged before submission.
      const value = responses[id];
      return value !== undefined && value !== null && value !== '';
    });
  }, [responses]);

  const resetResponses = useCallback(() => {
    // Clear both answers and photos so a new assessment starts from a true blank state.
    setResponses({});
    setPhotos({});
  }, []);

  const updatePhotos = useCallback((questionId: string, questionPhotos: QuestionPhoto[]) => {
    // Replace the full photo list for one question so upload/remove actions stay predictable.
    setPhotos(prev => ({ ...prev, [questionId]: questionPhotos }));
  }, []);

  const getPhotos = useCallback((questionId: string): QuestionPhoto[] => {
    return photos[questionId] || [];
  }, [photos]);

  return {
    responses,
    updateResponse,
    updateComment,
    getResponse,
    getComment,
    isComplete,
    resetResponses,
    photos,
    updatePhotos,
    getPhotos
  };
}
